// FD2G-FAC-001 dual-path API client.
//
// The learner course experience (PortalFd2gFacLearn.jsx +
// PortalFd2gFacResourceCenter.jsx) is reachable two ways:
//
//   1. As an authenticated LEARNER holding a bearer token minted at
//      enrollment time (`ccchaps.fd2gfac.token` in localStorage). All
//      calls go through /portal/fd2g-fac-001/* with an Authorization
//      header.
//
//   2. As an authenticated ADMIN using cookie auth via the existing
//      `../lib/api` axios instance. Calls go through the pre-existing
//      /admin/portal/preview/fd2gfac/* endpoints. Preserved intact for
//      internal QA.
//
// This module returns { get, post, mode } — mode is "learner" or "admin"
// so callers can adjust copy (e.g., preview banners) accordingly.

import axios from "axios";
import api from "../../lib/api";
import { previewStore } from "./previewStore";

const API = process.env.REACT_APP_BACKEND_URL;
const LS_TOKEN = "ccchaps.fd2gfac.token";
const FD2G_COURSE_CODE = "FD2G-FAC-001";

// Endpoint suffix → learner URL + admin URL.
// Learner path is entitlement-gated; admin path is admin-cookie-gated.
const PATH_MAP = {
  "course":                                 ["/portal/fd2g-fac-001/course",                                 "/admin/portal/preview/course"],
  "completion-state":                       ["/portal/fd2g-fac-001/completion-state",                       "/admin/portal/preview/fd2gfac/completion-state"],
  "orientation/acknowledge":                ["/portal/fd2g-fac-001/orientation/acknowledge",                "/admin/portal/preview/fd2gfac/orientation/acknowledge"],
  "session-assessment/submit":              ["/portal/fd2g-fac-001/session-assessment/submit",              "/admin/portal/preview/fd2gfac/session-assessment/submit"],
  "document/accept":                        ["/portal/fd2g-fac-001/document/accept",                        "/admin/portal/preview/fd2gfac/document/accept"],
  "final-assessment/submit":                ["/portal/fd2g-fac-001/final-assessment/submit",                "/admin/portal/preview/fd2gfac/final-assessment/submit"],
  "credential/issue":                       ["/portal/fd2g-fac-001/credential/issue",                       "/admin/portal/preview/fd2gfac/credential/issue"],
  "credential":                             ["/portal/fd2g-fac-001/credential",                             "/admin/portal/preview/fd2gfac/credential"],
  "credential/certificate.pdf":             ["/portal/fd2g-fac-001/credential/certificate.pdf",             "/admin/portal/preview/fd2gfac/credential/certificate.pdf"],
  "resource-center":                        ["/portal/fd2g-fac-001/resource-center",                        "/admin/portal/preview/fd2gfac/resource-center"],
  "resource-file":                          ["/portal/fd2g-fac-001/resource-file",                          "/admin/portal/preview/fd2gfac/resource-file"],
};

function readToken() {
  try {
    return localStorage.getItem(LS_TOKEN) || "";
  } catch { return ""; }
}

// Admin preview overrides any learner-side token in localStorage. If the
// admin has previously signed in as an FD2G learner in this browser (e.g.,
// during a demo), the previously-cached bearer token would otherwise be
// picked up and the admin-preview navigation would route through the
// entitlement-gated learner endpoints. previewStore.isActive() +
// getCourseCode() lets us short-circuit to the admin path unconditionally.
function isAdminPreviewActive() {
  try {
    return (
      previewStore.isActive() &&
      previewStore.getCourseCode() === FD2G_COURSE_CODE
    );
  } catch { return false; }
}

export function getFd2gApiMode() {
  if (isAdminPreviewActive()) return "admin";
  return readToken() ? "learner" : "admin";
}

// Resolve a key like "completion-state" or "resource-file/some-slug" to the
// URL for the current caller.
function resolve(key, mode) {
  // Handle keys with a variable tail — e.g. "resource-file/foo-bar".
  const [base, ...rest] = key.split("/");
  let joinedTail = rest.length ? "/" + rest.join("/") : "";
  const groupKey = joinedTail ? `${base}` : key;
  // Try exact match first, then group match (for /resource-file/*).
  const entry = PATH_MAP[key] || PATH_MAP[groupKey];
  if (!entry) throw new Error(`fd2gApi: unknown endpoint key "${key}"`);
  const idx = mode === "learner" ? 0 : 1;
  return entry[idx] + (PATH_MAP[key] ? "" : joinedTail);
}

export default function fd2gApi() {
  const mode = getFd2gApiMode();
  const token = readToken();

  if (mode === "learner") {
    const client = axios.create({
      baseURL: `${API}/api`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      mode,
      get: (key, config) => client.get(resolve(key, mode), config),
      post: (key, body, config) => client.post(resolve(key, mode), body, config),
    };
  }

  // Admin path — reuse the shared cookie-auth axios instance.
  return {
    mode,
    get: (key, config) => {
      const url = resolve(key, mode);
      // Special-case: admin course endpoint takes ?code= param.
      if (key === "course") {
        return api.get(url, { ...config, params: { ...(config?.params || {}), code: "FD2G-FAC-001" } });
      }
      return api.get(url, config);
    },
    post: (key, body, config) => api.post(resolve(key, mode), body, config),
  };
}
